import React, { useEffect, useRef } from 'react';
import { useEditor, useEditorState, useTiptap, Tiptap } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  BoldOutlined,
  ItalicOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import './RichTextEditor.css';

export default function RichTextEditor({ value = '', onChange }) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    extensions: [StarterKit],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChangeRef.current?.(editor.getHTML());
    },
  }, []);

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value ?? '';
    if (current !== next) {
      editor.commands.setContent(next, false);
    }
  }, [value, editor]);

  if (!editor) {
    return (
      <div className="rich-text-editor-wrap rich-text-editor-loading">
        <div className="rich-text-editor-content">
          <div className="rich-text-editor-placeholder">Loading editor…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rich-text-editor-wrap">
      <Tiptap editor={editor}>
        <RichTextEditorToolbar />
        <Tiptap.Content className="rich-text-editor-content" />
      </Tiptap>
    </div>
  );
}

function RichTextEditorToolbar() {
  const { editor } = useTiptap();
  useEditorState({
    editor,
    selector: (s) => s.transactionNumber,
  });
  if (!editor) return null;
  return (
    <div className="rich-text-editor-toolbar">
      <Button
        type="text"
        size="small"
        icon={<BoldOutlined />}
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'rich-text-editor-btn-active' : ''}
        title="Bold"
      />
      <Button
        type="text"
        size="small"
        icon={<ItalicOutlined />}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'rich-text-editor-btn-active' : ''}
        title="Italic"
      />
      <Button
        type="text"
        size="small"
        icon={<UnderlineOutlined />}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={editor.isActive('underline') ? 'rich-text-editor-btn-active' : ''}
        title="Underline"
      />
      <Button
        type="text"
        size="small"
        icon={<StrikethroughOutlined />}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={editor.isActive('strike') ? 'rich-text-editor-btn-active' : ''}
        title="Strikethrough"
      />
      <span className="rich-text-editor-toolbar-divider" />
      <Button
        type="text"
        size="small"
        icon={<UnorderedListOutlined />}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'rich-text-editor-btn-active' : ''}
        title="Bullet list"
      />
      <Button
        type="text"
        size="small"
        icon={<OrderedListOutlined />}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'rich-text-editor-btn-active' : ''}
        title="Numbered list"
      />
    </div>
  );
}
